import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ListParams } from "@/services/api";
import type {
  AmbassadorApplication,
  ContactSubmission,
  DominionDemoAccess,
  EventPitch,
  NewsletterSubscriber,
  Page,
  PartnershipApplication,
  WaitlistEntry,
} from "@/types";

/**
 * React Query hooks for the admin Website Forms feature. These wrap the
 * existing API client methods — the client is unchanged — and own the query
 * keys, caching, and invalidation-on-success that previously lived as a manual
 * `useEffect` loader + manual refetch in the page and its detail modal.
 *
 * The page shows seven form-submission lists (waitlist, partnerships, contacts,
 * newsletter, pitches, demo-access, ambassadors), each parameterised by page/search/sort. The list query
 * is keyed by form type + params, so changing the tab or any filter changes the
 * key and React Query refetches naturally. A categorization edit invalidates
 * the edited type's list keys. Follows the Sources slice template.
 *
 * CSV export stays a direct `api.export*CSV()` call (a one-shot file download,
 * not a query) — see the page's `handleExportCSV`.
 */

export type WebsiteFormType =
  | "waitlist"
  | "partnerships"
  | "contacts"
  | "newsletter"
  | "pitches"
  | "demo-access"
  | "ambassadors";

export type WebsiteFormEntry =
  | WaitlistEntry
  | PartnershipApplication
  | ContactSubmission
  | NewsletterSubscriber
  | EventPitch
  | DominionDemoAccess
  | AmbassadorApplication;

/** Categorization fields editable from the detail modal. */
export interface CategorizationData {
  cohort?: string | null;
  contact_status?: string;
  plan_type?: string | null;
}

export const websiteFormKeys = {
  all: ["admin", "websiteForms"] as const,
  type: (type: WebsiteFormType) => [...websiteFormKeys.all, type] as const,
  lists: (type: WebsiteFormType) => [...websiteFormKeys.type(type), "list"] as const,
  list: (type: WebsiteFormType, params: ListParams) =>
    [...websiteFormKeys.lists(type), params] as const,
};

const listFetchers: Record<
  WebsiteFormType,
  (params: ListParams) => Promise<Page<WebsiteFormEntry>>
> = {
  waitlist: (params) => api.getWaitlistEntries(params),
  partnerships: (params) => api.getPartnershipApplications(params),
  contacts: (params) => api.getContactSubmissions(params),
  newsletter: (params) => api.getNewsletterSubscribers(params),
  pitches: (params) => api.getEventPitches(params),
  "demo-access": (params) => api.getDominionDemoAccess(params),
  ambassadors: (params) => api.getAmbassadorApplications(params),
};

const categorizationUpdaters: Record<
  WebsiteFormType,
  (id: string, data: CategorizationData) => Promise<unknown>
> = {
  waitlist: (id, data) => api.updateWaitlistCategorization(id, data),
  partnerships: (id, data) => api.updatePartnershipCategorization(id, data),
  contacts: (id, data) => api.updateContactCategorization(id, data),
  newsletter: (id, data) => api.updateNewsletterCategorization(id, data),
  pitches: (id, data) => api.updatePitchCategorization(id, data),
  "demo-access": (id, data) => api.updateDominionDemoAccessCategorization(id, data),
  ambassadors: (id, data) => api.updateAmbassadorCategorization(id, data),
};

/** Paginated list for the given form type, keyed by type + params. */
export function useWebsiteForms(type: WebsiteFormType, params: ListParams) {
  return useQuery<Page<WebsiteFormEntry>>({
    queryKey: websiteFormKeys.list(type, params),
    queryFn: () => listFetchers[type](params),
  });
}

/** Update categorization for an entry of `type`, invalidating that type's lists. */
export function useUpdateCategorization(type: WebsiteFormType) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CategorizationData }) =>
      categorizationUpdaters[type](id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: websiteFormKeys.type(type) }),
  });
}
